/**
 * StatHolidayEditDialog.tsx
 * Create / edit dialog for a single StatHoliday (Phase 1, Item 7).
 *
 * Props:
 *   holiday  — the holiday being edited (null = create mode)
 *   open     — controls visibility
 *   onClose  — called on cancel
 *   onSaved  — called after a successful save (receives the saved holiday)
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import {
  createStatHoliday,
  updateStatHoliday,
} from '../../../services/scheduleStatHolidays';
import type { StatHoliday } from '../../../types/schedule';

// Canadian provinces the lab tags holidays against (NB first — the lab's home region).
const REGION_OPTIONS = ['NB', 'BC', 'AB', 'ON', 'QC', 'NS', 'MB', 'SK', 'PE', 'NL'];

interface Props {
  holiday: StatHoliday | null;
  open: boolean;
  onClose: () => void;
  onSaved: (saved: StatHoliday) => void;
  /** Region to pre-select when creating a new holiday (defaults to NB). */
  defaultRegion?: string;
}

export const StatHolidayEditDialog = ({ holiday, open, onClose, onSaved, defaultRegion = 'NB' }: Props) => {
  const isCreate = holiday === null;

  const [date, setDate] = useState('');
  const [name, setName] = useState('');
  const [region, setRegion] = useState(defaultRegion);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Populate form when editing an existing holiday
  useEffect(() => {
    if (holiday) {
      // HolidayDate may come back as full ISO datetime from MSSQL — take first 10 chars
      setDate(holiday.HolidayDate.slice(0, 10));
      setName(holiday.Name);
      setRegion(holiday.Region ?? defaultRegion);
    } else {
      setDate('');
      setName('');
      setRegion(defaultRegion);
    }
    setError(null);
  }, [holiday, open, defaultRegion]);

  const handleSave = async () => {
    if (!date.trim()) { setError('Date is required'); return; }
    if (!name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      let saved: StatHoliday;
      if (isCreate) {
        saved = await createStatHoliday({ HolidayDate: date, Name: name.trim(), Region: region });
      } else {
        saved = await updateStatHoliday(holiday.StatHolidayKey, {
          HolidayDate: date,
          Name: name.trim(),
          Region: region,
        });
      }
      onSaved(saved);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string; message?: string } } };
      setError(e?.response?.data?.error ?? e?.response?.data?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isCreate ? 'Add Statutory Holiday' : 'Edit Statutory Holiday'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
            helperText="ISO format: YYYY-MM-DD"
          />
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            placeholder="e.g. New Brunswick Day"
          />
          <TextField
            label="Region"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            select
            fullWidth
            size="small"
          >
            {REGION_OPTIONS.map((r) => (
              <MenuItem key={r} value={r}>{r}</MenuItem>
            ))}
          </TextField>
          {error && (
            <Typography variant="caption" color="error">{error}</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: 'none' }}
        >
          {saving ? 'Saving…' : isCreate ? 'Add' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
